import { Link } from 'react-router-dom'
import type { DeliveryBar } from '../../utils/dailyDelivery'

/**
 * The daily missed-messages bars, one per UTC day, oldest on the left.
 *
 * Colour is missed messages (exact): red when any went missing, green when
 * none did, grey when the day has no estimate or nothing was heard. Height is
 * received against expected (the estimate). Shared by the device card and the
 * dashboard's worst-devices list so the two always read the same way.
 *
 * With `hrefForDay` each bar is a link to that day's detail. Without it the
 * bars are a picture only, for lists where the row itself is the link - twenty
 * strips of ninety links each would be thousands of tab stops.
 */

const TONES: Record<DeliveryBar['tone'], string> = {
  ok: 'bg-emerald-400 group-hover:bg-emerald-500',
  missed: 'bg-red-500 group-hover:bg-red-600',
  unknown: 'bg-gray-300 group-hover:bg-gray-400',
}

/** A UTC day key as "Sep 23": the strip already spans a known window, so no year. */
export function shortDay(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

/**
 * The hovered bar's date, floated above the strip. Its parent must be a
 * `group relative` bar slot. Hidden from screen readers: the slot's title or
 * label already carries the date.
 */
export function BarDateLabel({ day }: { day: string }) {
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 hidden -translate-x-1/2 whitespace-nowrap rounded bg-gray-800 px-1.5 py-0.5 text-[11px] tabular-nums text-white group-hover:block group-focus-visible:block"
    >
      {shortDay(day)}
    </span>
  )
}

export function deliveryBarTitle(bar: DeliveryBar): string {
  const { day, row, expected, partial } = bar
  if (bar.empty) return `${day}: no messages received`
  const expectedText =
    expected === null ? 'expected not estimated (reconfigured)' : `${expected} expected${partial ? ' so far' : ''}`
  return `${day}: ${row.missedMessages} missed, ${row.receivedReadings} received, ${expectedText}`
}

export default function DeliveryBars({
  bars,
  heightClass = 'h-10',
  hrefForDay,
  maxBarWidth = 26,
  stretch = false,
  showDates = true,
  titleSuffix,
}: {
  bars: DeliveryBar[]
  /** Extra text appended to a bar's tooltip, e.g. which base stations carried the day. */
  titleSuffix?: (day: string) => string
  heightClass?: string
  hrefForDay?: (day: string) => string
  /** Caps the strip's width so a short window does not stretch a few bars. */
  maxBarWidth?: number
  /** Fill the available width regardless of bar count, for rows of strips that must line up. */
  stretch?: boolean
  showDates?: boolean
}) {
  if (bars.length === 0) return null

  return (
    <div style={stretch ? undefined : { maxWidth: bars.length * maxBarWidth }}>
      <div className={`flex items-end gap-px ${heightClass}`}>
        {bars.map((bar) => {
          const title = deliveryBarTitle(bar) + (titleSuffix?.(bar.day) ?? '')
          const fill = (
            <span
              className={`w-full rounded-sm ${TONES[bar.tone]} ${bar.partial ? 'opacity-70' : ''}`}
              style={{ height: bar.empty ? '8%' : `${Math.max(bar.fill * 100, 8)}%` }}
            />
          )
          return hrefForDay ? (
            <Link
              key={bar.day}
              to={hrefForDay(bar.day)}
              title={title}
              aria-label={title}
              className="group relative flex h-full min-w-0 flex-1 items-end"
            >
              <BarDateLabel day={bar.day} />
              {fill}
            </Link>
          ) : (
            <span key={bar.day} title={title} className="group relative flex h-full min-w-0 flex-1 items-end">
              <BarDateLabel day={bar.day} />
              {fill}
            </span>
          )
        })}
      </div>
      {showDates && (
        <div className="mt-1 flex justify-between gap-2 text-[11px] tabular-nums text-gray-500">
          <span>{bars[0].day}</span>
          {bars.length > 1 && <span>{bars[bars.length - 1].day}</span>}
        </div>
      )}
    </div>
  )
}

/** The three colours in words, so the bars never rely on colour alone. */
export function DeliveryLegend() {
  const items: [string, string][] = [
    ['bg-red-500', 'Missed'],
    ['bg-emerald-400', 'None missed'],
    ['bg-gray-300', 'No data'],
  ]
  return (
    <ul className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-600">
      {items.map(([tone, label]) => (
        <li key={label} className="flex items-center gap-1">
          <span aria-hidden="true" className={`h-2.5 w-2.5 rounded-sm ${tone}`} />
          {label}
        </li>
      ))}
      <li className="text-gray-500">· taller = more received</li>
    </ul>
  )
}
